import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { parseISO } from 'date-fns';
import cn from 'classnames';

import { ROOT } from 'src/constants/routes';
import { useAppContext } from 'src/context/AppContext';

import { buildLifetimeSegments, Category, CATEGORY_COLORS, getLifetimeLegend, getLifetimeYearAxis } from '../utils';

import css from './LifeTime.module.scss';

const CATEGORY_CSS = {
  [Category.WAR]: css.w,
  [Category.MILITARY_OPERATION]: css.mo,
  [Category.TERRORIST_OPERATIONS]: css.to,
} as const;

export const LifeTime = () => {
  const [searchParams] = useSearchParams();
  const birthDateParam = searchParams.get('d');
  const { personalStats } = useAppContext();

  if (!birthDateParam) {
    return <Navigate to={ROOT} />;
  }

  if (!personalStats) {
    return null;
  }

  const birthdate = parseISO(birthDateParam);
  const today = new Date();
  const { stats, timeline } = personalStats;
  const segments = buildLifetimeSegments(birthdate, timeline, stats.lifeTimeTotalDays);
  const { birthYear, endYear, tickCount } = getLifetimeYearAxis(birthdate, today);
  const legend = getLifetimeLegend();
  const tickSeparators = Math.max(0, tickCount - 2);

  return (
    <section className={css.lifeTime} dir="rtl" aria-label="ציר חיים">
      <ul className={css.legend}>
        {legend.map(({ category, label }) => (
          <li key={category} className={css.legendItem}>
            <span
              className={category === 'peaceful' ? css.legendDotPeaceful : css.legendDot}
              style={{ backgroundColor: CATEGORY_COLORS[category] }}
            />
            <span className={css.legendLabel}>{label}</span>
          </li>
        ))}
      </ul>

      <div className={css.barBlock}>
        <div className={css.bar} role="img" aria-hidden>
          {segments.map((segment, index) => (
            <div
              key={`${segment.category}-${index}`}
              className={cn(css.barSegment, CATEGORY_CSS[segment.category])}
              style={{
                left: `${segment.lifeTimeStartPercent}%`,
                width: `${segment.widthPercent}%`,
              }}
            />
          ))}
        </div>

        <div className={css.years}>
          <span className={css.yearLabel}>{birthYear}</span>
          {Array.from({ length: tickSeparators }, (_, index) => (
            <span key={index} className={css.yearTick} aria-hidden>
              |
            </span>
          ))}
          <span className={css.yearLabel}>{endYear}</span>
        </div>
      </div>
    </section>
  );
};

export default LifeTime;
